/**
 * Rol del usuario actual en Operaciones (Fase R). Lee tenant_ops_members; si no
 * hay asignación, cae a 'admin' (no rompe a nadie). El founder asigna roles
 * restringidos (logística/contabilidad) desde la Configuración de Operaciones.
 */
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@smarty/shared';
import { supabase } from '@/lib/supabase';
import { opsCaps, type OpsRole, type OpsCaps } from '@/lib/operations/orderMath';

export interface UseOpsRoleResult {
  loading: boolean;
  role: OpsRole;
  caps: OpsCaps;
}

export function useOpsRole(): UseOpsRoleResult {
  const { tenantId, user } = useAuth();
  const uid = (user as { id?: string } | null)?.id;

  /**
   * 🚨 Antes esto era `useEffect` + `useState`, y lo usan QUINCE pantallas. Cada montaje de cada una
   * disparaba su propia consulta por el rol — el mismo dato, una y otra vez, en cada navegación.
   *
   * Con la caché compartida, la primera pantalla lo pide y las otras catorce lo leen de memoria: la
   * `queryKey` es la misma, así que TanStack Query deduplica las consultas simultáneas y reutiliza el
   * resultado. Es exactamente para lo que existe esta capa.
   *
   * Dato de referencia: cambia cuando el founder reasigna un rol, o sea casi nunca. El `staleTime`
   * global de 30 s más la revalidación al volver a la pestaña alcanzan de sobra.
   */
  const { data, isPending } = useQuery({
    queryKey: ['ops-role', tenantId, uid],
    enabled: !!tenantId && !!uid,
    queryFn: async (): Promise<OpsRole> => {
      const { data: fila } = await supabase
        .from('tenant_ops_members')
        .select('ops_role')
        .eq('tenant_id', tenantId!)
        .eq('user_id', uid!)
        .maybeSingle();
      // Sin asignación explícita: 'admin'. No romper a nadie es más importante que ser estricto —
      // el founder asigna roles restringidos a propósito, la ausencia no significa "sin permisos".
      return (fila?.ops_role as OpsRole | undefined) ?? 'admin';
    },
  });

  const role = data ?? 'admin';
  // 🪤 `loading` es false cuando no hay a quién consultar (sin sesión o sin tenant): con `enabled`
  // en false, `isPending` se queda en true PARA SIEMPRE y las pantallas quedarían cargando eternamente.
  return { loading: !!tenantId && !!uid && isPending, role, caps: opsCaps(role) };
}
